import React, { useState } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { Checkbox, FormField, Icon } from '@legalshield/adonis-ux-framework';

import { AreaOfLawCategory } from '../types';

type CategoryConfig = { categoryName: string; choices: string[] };

type Props<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  categories: CategoryConfig[];
  required?: boolean;
};

const findCategoryValue = (categories: AreaOfLawCategory[], categoryName: string): AreaOfLawCategory | undefined =>
  categories.find((c) => c.category === categoryName);

const toggleChoice = (current: AreaOfLawCategory[], categoryName: string, choice: string): AreaOfLawCategory[] => {
  const existing = findCategoryValue(current, categoryName);
  if (!existing) {
    return [...current, { category: categoryName, choices: [choice] }];
  }
  const has = existing.choices.includes(choice);
  const updatedChoices = has ? existing.choices.filter((c) => c !== choice) : [...existing.choices, choice];
  const next = current
    .map((c) => (c.category === categoryName ? { ...c, choices: updatedChoices } : c))
    .filter((c) => c.choices.length > 0);
  return next;
};

const setCategoryAll = (
  current: AreaOfLawCategory[],
  category: CategoryConfig,
  selectAll: boolean,
): AreaOfLawCategory[] => {
  const withoutCat = current.filter((c) => c.category !== category.categoryName);
  if (!selectAll) return withoutCat;
  return [...withoutCat, { category: category.categoryName, choices: [...category.choices] }];
};

const AccordionMultiSelect = <T extends FieldValues>({ control, name, categories, required }: Props<T>) => {
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());

  const toggleOpen = (cat: string) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const value = (field.value as AreaOfLawCategory[] | undefined) ?? [];
        return (
          <FormField
            id={String(name)}
            required={required}
            status={fieldState.error ? 'invalid' : undefined}
            validationHint={fieldState.error?.message}
          >
            <div className="rnq-accordion">
              {categories.map((cat) => {
                const isOpen = openCategories.has(cat.categoryName);
                const selected = findCategoryValue(value, cat.categoryName);
                const selectedCount = selected?.choices.length ?? 0;
                const allSelected = selectedCount === cat.choices.length;
                return (
                  <div key={cat.categoryName} className="rnq-accordion__category">
                    <div
                      className="rnq-accordion__header"
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleOpen(cat.categoryName)}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleOpen(cat.categoryName)}
                    >
                      <span>
                        <strong>{cat.categoryName}</strong>
                        {selectedCount > 0 ? (
                          <span style={{ marginLeft: 8, color: '#0057b7' }}>
                            ({selectedCount}/{cat.choices.length})
                          </span>
                        ) : null}
                      </span>
                      <Icon name={isOpen ? 'nav_chevron_single_up' : 'nav_chevron_single_down'} />
                    </div>
                    {isOpen && (
                      <div className="rnq-accordion__choices">
                        <Checkbox
                          name={`${cat.categoryName}-all`}
                          rightLabel="Select all in category"
                          checked={allSelected}
                          onChange={() => field.onChange(setCategoryAll(value, cat, !allSelected))}
                        />
                        {cat.choices.map((choice) => (
                          <Checkbox
                            key={choice}
                            name={`${cat.categoryName}-${choice}`}
                            rightLabel={choice}
                            checked={Boolean(selected?.choices.includes(choice))}
                            onChange={() => field.onChange(toggleChoice(value, cat.categoryName, choice))}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </FormField>
        );
      }}
    />
  );
};

export default AccordionMultiSelect;
